import {Button, ButtonGroup, DialogFooter, Radio} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';

import {KeyboardEvent, useState} from 'react';

import {includes, keys} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  isPhotoDownloadSizePreset,
  PHOTO_DOWNLOAD_SIZE_PRESETS,
  PhotoDownloadSizePreset,
} from '../../constants/photoDownloadSizePresets';

interface PhotoDownloadSizeDialogProps extends RequiredTestIdProps {
  defaultPreset: PhotoDownloadSizePreset;
  /** The parent closes the dialog. */
  onConfirm: (preset: PhotoDownloadSizePreset) => void;
  onCancel: () => void;
}

const ARROW_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];

/**
 * The lightbox changes the slide on arrow keys from a `document` listener. Stops the arrow keys in
 * the dialog, so they change only the selected size. No `preventDefault`: radio navigation stays.
 */
const stopArrowKeyPropagation = (event: KeyboardEvent<HTMLDivElement>) => {
  if (includes(event.key, ARROW_KEYS)) {
    event.stopPropagation();
  }
};

const getPresetOptions = () =>
  keys(PHOTO_DOWNLOAD_SIZE_PRESETS).map((preset) => ({
    value: preset,
    label: PHOTO_DOWNLOAD_SIZE_PRESETS[preset].label ?? i18n.t('entity.vehicle.labels.original'),
  }));

export function PhotoDownloadSizeDialog(props: PhotoDownloadSizeDialogProps) {
  const [selectedPreset, setSelectedPreset] = useState<PhotoDownloadSizePreset>(
    props.defaultPreset
  );

  const handleChange = (value: string | null) => {
    if (isPhotoDownloadSizePreset(value)) {
      setSelectedPreset(value);
    }
  };

  const handleConfirm = () => props.onConfirm(selectedPreset);

  return (
    <Box onKeyDownCapture={stopArrowKeyPropagation}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.photo.labels.downloadSizeDescription')}
        </Text>
        <Radio
          name="photoDownloadSize"
          direction="column"
          spacing={2}
          options={getPresetOptions()}
          value={selectedPreset}
          onChange={handleChange}
          data-testid={suffixTestId('preset', props)}
        />
        <DialogFooter data-testid={props['data-testid']}>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              onClick={props.onCancel}
              title={i18n.t('general.actions.cancel')}
              data-testid={suffixTestId('cancel', props)}
            />
            <Button
              variant="primary"
              onClick={handleConfirm}
              title={i18n.t('general.actions.download')}
              data-testid={suffixTestId('confirm', props)}
            />
          </ButtonGroup>
        </DialogFooter>
      </VStack>
    </Box>
  );
}
